import React, { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  AddEntityInput,
  CreateSponsorInput,
  SponsorBusinessEntityDto,
  SponsorDto,
  SponsorType,
  useAddEntity,
  useCreateSponsor,
  useDeleteSponsor,
  useRemoveEntity,
  useSponsors,
  useUpdateSponsor,
} from '@shared/api/hooks/useSponsors';
import { MediaOrUrlField } from '@shared/components/MediaOrUrlField';

const TYPE_LABEL: Record<SponsorType, string> = {
  SINGLE_STORE: 'Singolo negozio',
  SHOPPING_MALL: 'Centro commerciale',
};

interface SponsorFormValues {
  name: string;
  type: SponsorType;
  address?: string;
  logoUrl?: string;
  isActive?: boolean;
  participatesInLoyalty?: boolean;
}

interface EntityFormValues {
  partitaIva: string;
  businessName: string;
  storeIdentifier?: string;
  aliases?: string[];
}

/**
 * P.IVA / codice fiscale: solo cifre e lettere MAIUSCOLE, come il DTO server
 * (@Matches(/^[0-9A-Z]+$/)). Ripulisce un incollato tipo "01234 567 890" o
 * "IT-01234567890" togliendo spazi e punteggiatura, così la richiesta parte
 * già valida invece di sbattere su un 400 lato server.
 */
export function normalizePartitaIva(v: unknown): unknown {
  return typeof v === 'string' ? v.toUpperCase().replace(/[^0-9A-Z]/g, '') : v;
}

/** Toglie alias vuoti e ripulisce gli spazi ai bordi. */
export function cleanAliases(v?: string[]): string[] {
  return (v ?? []).map((a) => a.trim()).filter((a) => a.length > 0);
}

const ENTITY_FIELDS = ['partitaIva', 'businessName', 'storeIdentifier', 'aliases'] as const;
const SPONSOR_FIELDS = ['name', 'type', 'logoUrl', 'address', 'isActive', 'participatesInLoyalty'] as const;

/**
 * Rete di sicurezza (solo questa pagina): quando il backend risponde 400 di
 * validazione, il suo messaggio deve comparire NEL FORM accanto al campo che lo
 * causa, non solo in console. class-validator restituisce `message` come array
 * di stringhe (una per vincolo). Mappa ogni messaggio sul campo che nomina; ciò
 * che non nomina un campo noto torna come messaggio generico.
 */
export function splitServerFieldErrors(
  e: unknown,
  fields: readonly string[] = ENTITY_FIELDS,
): { fieldErrors: { name: string; errors: string[] }[]; others: string[] } {
  const raw = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  const msgs = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
  const byField = new Map<string, string[]>();
  const others: string[] = [];
  for (const m of msgs) {
    const field = fields.find((f) => m.toLowerCase().includes(f.toLowerCase()));
    if (field) byField.set(field, [...(byField.get(field) ?? []), m]);
    else others.push(m);
  }
  return {
    fieldErrors: [...byField.entries()].map(([name, errors]) => ({ name, errors })),
    others,
  };
}

export function SponsorsPage(): React.ReactElement {
  const [sponsorModal, setSponsorModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [entitySponsor, setEntitySponsor] = useState<string | null>(null);

  const [form] = Form.useForm<SponsorFormValues>();
  const [entityForm] = Form.useForm<EntityFormValues>();

  const { data, isLoading, refetch, isFetching } = useSponsors();
  const sponsors: SponsorDto[] = data?.data ?? [];

  const createMutation = useCreateSponsor();
  const updateMutation = useUpdateSponsor();
  const deleteMutation = useDeleteSponsor();
  const addEntityMutation = useAddEntity();
  const removeEntityMutation = useRemoveEntity();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    setSponsorModal(true);
  };
  const openEdit = (s: SponsorDto): void => {
    setEditId(s.id);
    form.setFieldsValue({
      name: s.name,
      type: s.type,
      address: s.address ?? undefined,
      logoUrl: s.logoUrl ?? undefined,
      isActive: s.isActive,
      participatesInLoyalty: s.participatesInLoyalty,
    });
    setSponsorModal(true);
  };

  const onSubmitSponsor = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input: v });
        message.success('Sponsor aggiornato');
      } else {
        const input: CreateSponsorInput = {
          name: v.name,
          type: v.type,
          address: v.address || undefined,
          logoUrl: v.logoUrl || undefined,
          participatesInLoyalty: v.participatesInLoyalty ?? false,
        };
        await createMutation.mutateAsync(input);
        message.success('Sponsor creato');
      }
      setSponsorModal(false);
    } catch (e) {
      // Stesso trattamento del gestore delle ragioni sociali: il 400 di
      // validazione compare inline sul campo, l'array dei messaggi viene unito.
      const { fieldErrors, others } = splitServerFieldErrors(e, SPONSOR_FIELDS);
      if (fieldErrors.length) form.setFields(fieldErrors as Parameters<typeof form.setFields>[0]);
      if (others.length) message.error(others.join('; '));
      else if (!fieldErrors.length) message.error('Errore');
    }
  };

  const onSubmitEntity = async (): Promise<void> => {
    if (!entitySponsor) return;
    const v = await entityForm.validateFields();
    try {
      const aliases = cleanAliases(v.aliases);
      const input: AddEntityInput = {
        partitaIva: v.partitaIva.toUpperCase(),
        businessName: v.businessName,
        storeIdentifier: v.storeIdentifier || undefined,
        aliases: aliases.length ? aliases : undefined,
      };
      await addEntityMutation.mutateAsync({ sponsorId: entitySponsor, input });
      message.success('Ragione sociale aggiunta');
      entityForm.resetFields();
      setEntitySponsor(null);
    } catch (e) {
      // Rete di sicurezza: un 400 di validazione del backend compare inline nel
      // form, accanto al campo che lo causa, non solo in console.
      const { fieldErrors, others } = splitServerFieldErrors(e);
      if (fieldErrors.length)
        entityForm.setFields(fieldErrors as Parameters<typeof entityForm.setFields>[0]);
      if (others.length) message.error(others.join('; '));
      else if (!fieldErrors.length) message.error('Errore');
    }
  };

  const onDeleteSponsor = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Sponsor eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const onRemoveEntity = async (sponsorId: string, entityId: string): Promise<void> => {
    try {
      await removeEntityMutation.mutateAsync({ sponsorId, entityId });
      message.success('Ragione sociale rimossa');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Sponsor locali
          </Typography.Title>
          <Tag>{sponsors.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo sponsor
          </Button>
        </Space>
      }
    >
      <Table<SponsorDto>
        rowKey="id"
        loading={isLoading}
        dataSource={sponsors}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (s) => (
            <Card
              size="small"
              title={
                <Space>
                  <ShopOutlined />
                  Ragioni sociali ({s.businessEntities.length})
                </Space>
              }
              extra={
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    entityForm.resetFields();
                    setEntitySponsor(s.id);
                  }}
                >
                  Aggiungi
                </Button>
              }
            >
              <Table<SponsorBusinessEntityDto>
                rowKey="id"
                size="small"
                dataSource={s.businessEntities}
                pagination={false}
                columns={[
                  { title: 'Partita IVA', dataIndex: 'partitaIva', width: 150 },
                  { title: 'Ragione sociale', dataIndex: 'businessName' },
                  {
                    title: 'Identificativo negozio',
                    dataIndex: 'storeIdentifier',
                    width: 170,
                    render: (v: string | null) => v ?? '—',
                  },
                  {
                    title: 'Alias',
                    dataIndex: 'aliases',
                    render: (v: string[] | null) =>
                      v && v.length ? (
                        <Space size={[0, 4]} wrap>
                          {v.map((a) => (
                            <Tag key={a}>{a}</Tag>
                          ))}
                        </Space>
                      ) : (
                        '—'
                      ),
                  },
                  {
                    title: '',
                    width: 60,
                    align: 'right',
                    render: (_v, e) => (
                      <Popconfirm
                        title="Rimuovere?"
                        okText="Rimuovi"
                        okButtonProps={{ danger: true }}
                        cancelText="Annulla"
                        onConfirm={() => onRemoveEntity(s.id, e.id)}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            </Card>
          ),
        }}
        columns={[
          { title: 'Nome', dataIndex: 'name' },
          {
            title: 'Tipo',
            dataIndex: 'type',
            width: 180,
            render: (v: SponsorType) => <Tag>{TYPE_LABEL[v]}</Tag>,
          },
          {
            title: 'Indirizzo',
            dataIndex: 'address',
            render: (v: string | null) => v ?? '—',
          },
          {
            title: 'Ragioni sociali',
            width: 130,
            render: (_v, r) => <Tag color="blue">{r.businessEntities.length}</Tag>,
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 100,
            render: (v: boolean) =>
              v ? <Tag color="green">Attivo</Tag> : <Tag color="default">Inattivo</Tag>,
          },
          {
            title: 'Loyalty',
            dataIndex: 'participatesInLoyalty',
            width: 110,
            render: (v: boolean) =>
              v ? <Tag color="gold">Raccolta punti</Tag> : <Tag color="default">—</Tag>,
          },
          {
            title: '',
            width: 90,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare sponsor (e tutte ragioni sociali)?"
                  okText="Elimina"
                  okButtonProps={{ danger: true }}
                  cancelText="Annulla"
                  onConfirm={() => onDeleteSponsor(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Sponsor create/edit */}
      <Modal
        title={editId ? 'Modifica sponsor' : 'Nuovo sponsor'}
        open={sponsorModal}
        onCancel={() => setSponsorModal(false)}
        onOk={onSubmitSponsor}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="Nome sponsor" rules={[{ required: true, max: 255 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Tipo" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'SINGLE_STORE', label: TYPE_LABEL.SINGLE_STORE },
                { value: 'SHOPPING_MALL', label: TYPE_LABEL.SHOPPING_MALL },
              ]}
            />
          </Form.Item>
          <Form.Item name="address" label="Indirizzo">
            <Input />
          </Form.Item>
          <Form.Item name="logoUrl" label="Logo">
            <MediaOrUrlField category="SPONSOR_LOGO" placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="participatesInLoyalty"
            label="Aderisce alla raccolta punti"
            initialValue={false}
            tooltip="Se attivo, lo sponsor compare in app nella sezione Loyalty › Sponsor aderenti"
          >
            <Select
              options={[
                { value: true, label: 'Sì — aderisce alla raccolta punti' },
                { value: false, label: 'No' },
              ]}
            />
          </Form.Item>
          {editId && (
            <Form.Item name="isActive" label="Stato">
              <Select
                options={[
                  { value: true, label: 'Attivo' },
                  { value: false, label: 'Inattivo' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Add entity */}
      <Modal
        title="Aggiungi ragione sociale"
        open={entitySponsor !== null}
        onCancel={() => setEntitySponsor(null)}
        onOk={onSubmitEntity}
        okText="Aggiungi"
        confirmLoading={addEntityMutation.isPending}
        destroyOnHidden
      >
        <Form form={entityForm} layout="vertical" preserve={false}>
          <Form.Item
            name="partitaIva"
            label="Partita IVA"
            normalize={normalizePartitaIva}
            rules={[
              { required: true, message: 'Partita IVA obbligatoria' },
              { min: 8, max: 20, message: 'Da 8 a 20 caratteri' },
              {
                pattern: /^[0-9A-Z]+$/,
                message: 'Solo cifre e lettere maiuscole, senza spazi o punteggiatura',
              },
            ]}
          >
            <Input placeholder="01234567890" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="businessName"
            label="Ragione sociale"
            rules={[{ required: true, max: 255 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="storeIdentifier"
            label="Identificativo negozio (opzionale)"
            extra="Per multi-negozio sotto stessa P.IVA (es. codice interno centro commerciale)"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="aliases"
            label="Alias / insegne (opzionale)"
            extra="Nomi alternativi che la AI può leggere sullo scontrino. Usati per il match quando la P.IVA è illeggibile. Premi Invio per aggiungere."
            rules={[
              {
                validator: (_r, val?: string[]) => {
                  const arr = cleanAliases(val);
                  if (arr.length > 20) return Promise.reject(new Error('Massimo 20 alias'));
                  if (arr.some((a) => a.length > 255))
                    return Promise.reject(new Error('Ogni alias può avere al massimo 255 caratteri'));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select mode="tags" tokenSeparators={[',']} placeholder="es. Bar dello Stadio, BAR STADIO SRL" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
